/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect } from "react";
import { useMediaQuery } from "@mui/material";
import {
	Body,
	Container,
	RewardItem,
	ItemImage,
	RewardAmountContainer,
	RewardAmount,
	Box,
	Items,
	Title,
	SubTitle,
	Button,
	ButtonContainer,
	Header,
	Footer
} from "./styles";
import useCalculateScale from "components/PopUpManager/hooks/useCalculateScale";
import CloseIconButton from "components/AppModalDialogsV2/Dialogs/lib/CloseIconButton";
import useRewardsById from "hooks/useRewardsById";
import { useNavigate } from "react-router-dom";
import { useLinks } from "hooks/useLinks";
import { sortWithSpecificId } from "utils/sort";
import { pwaAPI } from "redux/apis/pwa";
import { PWARewardResponse } from "interfaces/pwa";
import { pwaAssets } from "assets/cdn";
import { useWebStoreContext } from "contexts/WebStoreProvider";
import { usePopUpsV2 } from "components/PopUpManagerV2/hooks/usePopUpsV2";
import { useGoogleAnalyticsContext } from "contexts/GoogleAnalyticsProvider";
import { DisplayReward } from "components/DIP/types";
import { useImagePath } from "hooks/useImagePath";

interface PWARewardPopupProps {}

export const PWARewardPopup: React.FC<PWARewardPopupProps> = () => {
	const { getImage } = useImagePath("cdn");
	const backgroundImage = getImage(pwaAssets.rewardPopupBackground);
	const { data }: { data?: PWARewardResponse } =
		pwaAPI.endpoints.getRewardPWA.useQueryState("");
	const links = useLinks();
	const isPortrait = useMediaQuery("(orientation: portrait)");
	const navigate = useNavigate();
	const { syncUserTracker } = useWebStoreContext();
	const { close } = usePopUpsV2();
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();

	const displayRewards = useRewardsById(data?.rewardId as string);
	const scale = useCalculateScale(
		isPortrait ? 363 + 40 : 956 + 40,
		isPortrait ? 613 + 40 : 410 + 40
	);

	useEffect(() => {
		if (!data || !displayRewards?.[0]) {
			return;
		}
		const firstReward = displayRewards[0];
		const rewardAmount = Number(
			firstReward?.description?.toString().replace(/,/g, "")
		);
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "pwa_launch_reward_popup_shown",
			event_type: "pwa_launch_reward",
			action: "popup_shown",
			source: "pwa_launch_reward",
			reward_id: firstReward?.id,
			reward_type: firstReward?.singularLabel,
			value_int: rewardAmount,
			web_activity_location: "pwa"
		});
	}, [data?.rewardId, displayRewards, reportPlaygamiEvent]);

	const onClose = () => {
		//dispatch(onClosePopUp());
		close({ variant: "pwa-reward-popup" });
		syncUserTracker();
	};

	const orderedRewards: DisplayReward[] = sortWithSpecificId(
		displayRewards,
		"loyalty_points",
		"last"
	);

	const handleClickContinue = () => {
		const firstReward = orderedRewards?.[0];
		const rewardAmount = Number(
			firstReward?.description?.toString().replace(/,/g, "")
		);
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "pwa_launch_reward_popup_click",
			event_type: "pwa_launch_reward",
			action: "popup_click",
			source: "pwa_launch_reward",
			reward_id: firstReward?.id,
			reward_type: firstReward?.singularLabel,
			value_int: rewardAmount,
			web_activity_location: "pwa"
		});

		navigate(links.shop.path);
		onClose();
	};
	if (!data || !displayRewards) {
		return null;
	}

	return (
		<Container scale={scale}>
			<CloseIconButton onClose={onClose} />
			<Header style={{ backgroundImage: `url(${backgroundImage})` }} />
			<Body>
				<Title>
					<div>Thanks for installing!</div>
				</Title>

				<SubTitle>
					This is our way to say thanks for adding the Tiki Solitaire TriPeaks
					Web Shop to your device home screen.
				</SubTitle>

				<Items>
					{orderedRewards?.map((displayReward) => (
						<Box key={displayReward?.id}>
							<RewardItem>
								<ItemImage
									src={
										displayReward?.imageUrl || displayReward?.overrideImageUrl
									}
									alt=""
								/>
								<RewardAmountContainer>
									<RewardAmount style={{ paintOrder: "stroke fill" }}>
										{displayReward?.description}
									</RewardAmount>
								</RewardAmountContainer>
							</RewardItem>
						</Box>
					))}
				</Items>
				<Footer> Your Loyalty Points balance has been updated! </Footer>
				<ButtonContainer>
					<Button
						variant="contained"
						themeColor="secondary"
						sx={{
							maxWidth: isPortrait ? "90%" : "65%"
						}}
						onClick={handleClickContinue}
					>
						Continue Shopping
					</Button>
				</ButtonContainer>
			</Body>
		</Container>
	);
};
